import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Paper,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';

const Careers = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null,
    coverLetter: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const positions = [
    'Electrical Engineer',
    'Civil Engineer',
    'Project Manager',
    'Site Supervisor',
    'Safety Officer',
    'Quality Control Engineer',
    'Other',
  ];

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      const response = await fetch('http://localhost:5000/api/careers/apply', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Application submitted successfully!',
          severity: 'success',
        });
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          resume: null,
          coverLetter: '',
        });
      } else {
        throw new Error('Failed to submit application');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error submitting application. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography 
        variant="h2" 
        component="h1" 
        gutterBottom 
        align="center"
        sx={{
          color: '#BF0A30',
          fontWeight: 'bold',
          mb: 4
        }}
      >
        Join Our Team
      </Typography>
      <Typography 
        variant="h5" 
        color="text.secondary" 
        paragraph 
        align="center"
        sx={{ mb: 6 }}
      >
        We're always looking for talented individuals to join our growing team of electrical and civil engineering professionals
      </Typography>

      <Box
        component={Paper}
        elevation={3}
        sx={{
          p: { xs: 3, md: 6 },
          mt: 4,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#BF0A30',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#BF0A30',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#BF0A30',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                select
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#BF0A30',
                    },
                  },
                }}
              >
                {positions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Years of Experience"
                name="experience"
                type="number"
                value={formData.experience}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#BF0A30',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Cover Letter"
                name="coverLetter"
                multiline
                rows={4}
                value={formData.coverLetter}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#BF0A30',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{
                  backgroundColor: '#BF0A30',
                  '&:hover': { backgroundColor: '#a30d26' },
                  fontSize: '1.1rem',
                  py: 1.5,
                }}
              >
                Upload Resume
                <input
                  type="file"
                  name="resume"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={handleChange}
                />
              </Button>
              {formData.resume && (
                <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                  Selected file: {formData.resume.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                sx={{
                  backgroundColor: '#BF0A30',
                  '&:hover': { backgroundColor: '#a30d26' },
                  fontSize: '1.25rem',
                  py: 2,
                  mt: 2,
                }}
              >
                Submit Application
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Careers; 